import createDependableResource from 'utils/zustand/createDependableResource'
import { MoleMeta, NftDetails } from 'views/VvsMinerMoleMyMoles/types'
import { fetchMoles } from 'lib/infoApi/moles'
import { useUserMoleTokenIds } from 'state/minerMole/useUserMoleTokenIds'
import { useUserBoostedFarmsInfos } from 'state/farmBoost/useUserBoostedFarmsInfos'
import uniq from 'lodash/uniq'

export interface NftStakeDetails extends NftDetails {
  isStaked: boolean
}

interface UserMolesValue {
  moles: NftStakeDetails[]
  moleIdsWithStaked: string[]
}

const fetchUserMoles = async (): Promise<UserMolesValue> => {
  const moleTokenIds = useUserMoleTokenIds.getState().value
  const boostedFarmInfos = useUserBoostedFarmsInfos.getState().value

  const stakedMoleIds = Object.entries(boostedFarmInfos.boostedFarmsInfos).flatMap(
    ([_farmId, info]) => info.boostingNfts,
  )
  const moleIdsWithStaked = uniq([...moleTokenIds, ...stakedMoleIds])
  try {
    const molesData = await fetchMoles(moleIdsWithStaked)
    const moles = molesData.map<NftStakeDetails>((mole) => {
      return {
        ...moleDataToNftDetails(mole),
        isStaked: stakedMoleIds.includes(mole.id),
      }
    })

    return {
      moles,
      moleIdsWithStaked,
    }
  } catch (error) {
    console.warn('UserMoles metadata will fallback to default values, fetchMoles from info api failed:', error)

    return {
      moles: moleIdsWithStaked.map((id) => ({
        id,
        imgUrl: '',
        name: `#${id}`,
        rank: 0,
        attributes: [],
        rarity: 'common',
        isStaked: stakedMoleIds.includes(id),
      })),
      moleIdsWithStaked,
    }
  }
}

export const moleDataToNftDetails = (mole: MoleMeta): NftDetails => {
  return {
    id: mole.id,
    imgUrl: mole.image,
    name: mole.name,
    rank: mole.rank,
    attributes: mole.attributes,
    rarity: mole.rarity,
  }
}

/**
 * user moles (including staked moles) as dependableResource
 */
export const useUserMoles = createDependableResource<UserMolesValue>({
  initValue: {
    moles: [],
    moleIdsWithStaked: [],
  },
  getValue: fetchUserMoles,
  dependResources: [useUserMoleTokenIds, useUserBoostedFarmsInfos],
  dbgName: 'UserMoles',
})
